import { useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, Select, message, Space, InputNumber } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useQuery, useMutation } from 'react-query';
import { rulesAPI, mailingAPI } from '../api/endpoints';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RulesPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();
  const triggerType = Form.useWatch('trigger_type', form);
  const showDays = triggerType === 'days_before' || triggerType === 'days_after';

  const { data: rulesData, refetch } = useQuery('collection-rules', () => rulesAPI.list());
  const { data: templatesData } = useQuery('templates-for-rules', () => mailingAPI.listTemplates());
  const createMutation = useMutation(rulesAPI.create);
  const updateMutation = useMutation(({ id, data }) => rulesAPI.update(id, data));
  const deleteMutation = useMutation(rulesAPI.delete);

  const rules = rulesData?.data?.results || [];
  const templates = templatesData?.data?.results || [];

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Disparador',
      dataIndex: 'trigger_type',
      key: 'trigger_type',
      render: (type) => {
        const map = {
          'days_before': 'Días Antes',
          'days_after': 'Días Después',
          'on_due_date': 'En el Vencimiento',
        };
        return map[type] || type;
      },
    },
    {
      title: 'Días',
      key: 'trigger_days',
      render: (_, record) =>
        record.trigger_type === 'on_due_date' ? '—' : record.trigger_days,
    },
    {
      title: 'Prioridad',
      dataIndex: 'priority',
      key: 'priority',
    },
    {
      title: 'CC adicional',
      dataIndex: 'additional_emails',
      key: 'additional_emails',
      render: (emails) => {
        if (!emails || emails.length === 0) return '—';
        return `${emails.length} correo${emails.length > 1 ? 's' : ''}`;
      },
    },
    {
      title: 'Activa',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active) => (active ? 'Sí' : 'No'),
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="primary" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Button type="primary" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)} />
        </Space>
      ),
    },
  ];

  const handleEdit = (record) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: 'Eliminar Regla',
      content: '¿Está seguro que desea eliminar esta regla de cobranza?',
      okText: 'Sí',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteMutation.mutateAsync(id);
          message.success('Regla eliminada correctamente');
          refetch();
        } catch (error) {
          message.error('Error al eliminar regla');
        }
      },
    });
  };

  const onFinish = async (values) => {
    try {
      const data = {
        ...values,
        trigger_days: values.trigger_type === 'on_due_date' ? 0 : values.trigger_days,
        additional_emails: values.additional_emails || [],
      };
      if (editingId) {
        await updateMutation.mutateAsync({ id: editingId, data });
        message.success('Regla actualizada correctamente');
      } else {
        await createMutation.mutateAsync(data);
        message.success('Regla creada correctamente');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingId(null);
      refetch();
    } catch (error) {
      message.error('Error al guardar regla');
    }
  };

  return (
    <div>
      <Card
        title="Reglas de Cobranza"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingId(null);
              form.resetFields();
              setIsModalOpen(true);
            }}
          >
            Nueva Regla
          </Button>
        }
      >
        <Table dataSource={rules} columns={columns} rowKey="id" />
      </Card>

      <Modal
        title={editingId ? 'Editar Regla' : 'Nueva Regla de Cobranza'}
        open={isModalOpen}
        onOk={() => form.submit()}
        onCancel={() => setIsModalOpen(false)}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="name"
            label="Nombre de la Regla"
            rules={[{ required: true, message: 'Por favor ingresa el nombre' }]}
          >
            <Input placeholder="Ej: Recordatorio 5 días antes" />
          </Form.Item>

          <Form.Item
            name="trigger_type"
            label="Tipo de Disparador"
            rules={[{ required: true, message: 'Por favor selecciona un tipo' }]}
          >
            <Select>
              <Select.Option value="days_before">X días ANTES del vencimiento</Select.Option>
              <Select.Option value="days_after">X días DESPUÉS del vencimiento</Select.Option>
              <Select.Option value="on_due_date">En la fecha de vencimiento</Select.Option>
            </Select>
          </Form.Item>

          {showDays && (
            <Form.Item
              name="trigger_days"
              label="Número de Días"
              rules={[{ required: true, message: 'Por favor ingresa los días' }]}
            >
              <InputNumber min={1} />
            </Form.Item>
          )}

          <Form.Item
            name="email_template_id"
            label="Plantilla de Correo"
            rules={[{ required: true, message: 'Por favor selecciona una plantilla' }]}
          >
            <Select placeholder="Selecciona una plantilla">
              {templates.map((template) => (
                <Select.Option key={template.id} value={template.id}>
                  {template.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="priority"
            label="Prioridad"
            initialValue={0}
          >
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            name="max_emails_per_invoice"
            label="Máximo de Correos por Factura"
            initialValue={1}
          >
            <InputNumber min={1} />
          </Form.Item>

          <Form.Item
            name="grace_days"
            label="Días de gracia para {{payment_deadline}}"
            tooltip="Si lo defines, el comodín {{payment_deadline}} en la plantilla mostrará la fecha de vencimiento + estos días. Ej: 15 → vencimiento + 15 días."
          >
            <InputNumber min={0} placeholder="Ej: 15" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="additional_emails"
            label="Correos Adicionales de Notificación"
            tooltip="Estos correos recibirán el mismo email que el cliente cada vez que se ejecute la regla. Escribe una dirección y presiona Enter. Puedes pegar varias separadas por coma."
            initialValue={[]}
            rules={[
              {
                validator(_, emails) {
                  const invalid = (emails || []).filter(e => !emailRegex.test(e));
                  if (invalid.length > 0)
                    return Promise.reject(new Error(`Dirección(es) inválida(s): ${invalid.join(', ')}`));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Select
              mode="tags"
              placeholder="ej: gerencia@empresa.com"
              tokenSeparators={[',', ';']}
              open={false}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="is_active"
            label="Activa"
            initialValue={true}
          >
            <Select>
              <Select.Option value={true}>Sí</Select.Option>
              <Select.Option value={false}>No</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RulesPage;
